import { useEffect, useRef, useState, type PointerEvent } from "react";
import { itx } from "../../../src/translationItx/itx";
import { useOnChangeWatcher } from "../../../src/models/useOnChangeWatcher";
import c1_state from "../model/C1_State";
import { useModel } from "../model/C1_useModel";
import type { C1_Floor } from "../model/C1_Floor";
import type { C1_Room } from "../model/C1_Room";
import type { C1_Wall } from "../model/C1_Wall";
import type { C1_PointData } from "../types/C1_ModelTypes";
import type { Rect } from "../types/C1_AppTypes";
import { clientToPlan } from "./C1_planCoordinates";
import { snapFreeWallEndpoint } from "../furniture/C1_freeWallSnap";

type Segment = { start: C1_PointData; end: C1_PointData };
type WallDrag = Segment & { pointerId: number; point: C1_PointData; view: Rect; handle: "start" | "end" | "move"; preview: Segment; snapped?: boolean; moved?: boolean };

export function C1_FreeWallsView({ floor, structureOpacity = 1, clipRoom }: { floor: C1_Floor; structureOpacity?: number; clipRoom?: C1_Room }) {
  useModel(c1_state);
  useOnChangeWatcher(floor.owner);
  const clipId = `c1-free-wall-clip-${floor.id}-${clipRoom?.id}`;
  return <g data-c1-svg-layer="free-walls" className="C1_wallLayer C1_freeWallLayer">
    {clipRoom && <defs><clipPath id={clipId}>
      <polygon points={clipRoom.polygonPoints().map(point => `${(point.x - c1_state.view.x1) * c1_state.svgPx},${(point.y - c1_state.view.y1) * c1_state.svgPx}`).join(" ")} />
    </clipPath></defs>}
    <g clipPath={clipRoom ? `url(#${clipId})` : undefined}>
      {floor.freeWalls.map(wall => <FreeWall key={wall.id} wall={wall} floor={floor} structureOpacity={structureOpacity} />)}
    </g>
  </g>;
}

function FreeWall({ wall, floor, structureOpacity }: { wall: C1_Wall; floor: C1_Floor; structureOpacity: number }) {
  useModel(c1_state);
  useOnChangeWatcher(wall);
  const drag = useRef<WallDrag | null>(null);
  const [preview, setPreview] = useState<Segment | null>(null);
  const [hovered, setHovered] = useState(false);
  const exporting = c1_state.floorImageRenderOptions.value !== null;
  const interactive = !exporting && c1_state.editMode.value === "building" && c1_state.currentFloorId.value === floor.id;
  const selected = interactive && c1_state.selectedWall === wall;
  const view = c1_state.view, PX = c1_state.svgPx, zoom = c1_state.zoom.value;
  const geometry = preview ?? wall;
  const x = (point: C1_PointData) => (point.x - view.x1) * PX;
  const y = (point: C1_PointData) => (point.y - view.y1) * PX;
  const width = c1_state.project.settings.wallThicknessInterior * PX;

  useEffect(() => {
    if (!preview) return;
    const cancel = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      drag.current = null;
      setPreview(null);
    };
    window.addEventListener("keydown", cancel);
    return () => window.removeEventListener("keydown", cancel);
  }, [preview !== null]);

  const startDrag = (event: PointerEvent<SVGGElement>, handle: WallDrag["handle"]) => {
    if (!interactive || event.button !== 0) return;
    const point = clientToPlan(event.currentTarget.closest(".C1_canvas"), event.clientX, event.clientY);
    if (!point) return;
    event.preventDefault();
    event.stopPropagation();
    c1_state.setWallSelection(wall);
    const segment = { start: { ...wall.start }, end: { ...wall.end } };
    drag.current = { ...segment, point, pointerId: event.pointerId, handle, view: { ...view }, preview: segment };
    setPreview(segment);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const move = (event: PointerEvent<SVGGElement>) => {
    const current = drag.current;
    if (!current || current.pointerId !== event.pointerId) return;
    const point = clientToPlan(event.currentTarget.closest(".C1_canvas"), event.clientX, event.clientY, current.view);
    if (!point) return;
    event.stopPropagation();
    const dx = point.x - current.point.x, dy = point.y - current.point.y;
    current.moved ||= Math.hypot(dx, dy) > 1e-6;
    if (!current.moved) return;
    const shift = (end: C1_PointData) => ({ x: end.x + dx, y: end.y + dy });
    const next = { start: current.handle === "end" ? current.start : shift(current.start),
      end: current.handle === "start" ? current.end : shift(current.end) };
    if (current.handle !== "move") {
      const segments = floor.rooms.flatMap(room => Array.from(room.walls ?? []).flatMap(candidate => {
        const points = candidate.visible && room.wallPoints(candidate);
        return points ? [points] : [];
      }));
      segments.push(...floor.freeWalls.filter(candidate => candidate !== wall && candidate.visible && !candidate.isDeleted));
      const snap = snapFreeWallEndpoint(next[current.handle], segments, event.shiftKey, undefined, {
        axes: c1_state.axes,
        anchor: current.handle === "start" ? current.end : current.start,
      });
      next[current.handle] = snap.point;
      current.snapped = snap.snapped;
    }
    if (Math.hypot(next.end.x - next.start.x, next.end.y - next.start.y) < 0.01) return;
    current.preview = next;
    setPreview(next);
  };

  const finish = (event: PointerEvent<SVGGElement>) => {
    const current = drag.current;
    if (!current || current.pointerId !== event.pointerId) return;
    if (event.type === "pointerup") move(event);
    drag.current = null;
    setPreview(null);
    event.stopPropagation();
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    const next = current.preview;
    if (event.type !== "pointerup" || wall.isDeleted || c1_state.currentFloorId.value !== floor.id ||
      (next.start.x === current.start.x && next.start.y === current.start.y && next.end.x === current.end.x && next.end.y === current.end.y)) return;
    state.undoManager.runAsSingleStep(itx("c1.freeWall.move", "Freie Wand verändern"), () => {
      (floor.owner?.c1 ?? floor).moveFreeWall(wall, next.start, next.end);
    });
  };

  return <g data-c1-free-wall-id={wall.id} data-testid={`c1-free-wall-${wall.id}`}
    opacity={selected || preview ? 1 : structureOpacity} pointerEvents={interactive ? "all" : "none"}
    style={{ cursor: interactive ? "default" : undefined }}
    onPointerEnter={() => { if (interactive) setHovered(true); }} onPointerLeave={() => setHovered(false)}
    onPointerMove={move} onPointerUp={finish} onPointerCancel={finish} onLostPointerCapture={finish}>
    <title>{itx("c1.freeWall.name", "Freie Wand")}</title>
    <line x1={x(geometry.start)} y1={y(geometry.start)} x2={x(geometry.end)} y2={y(geometry.end)}
      className={interactive && (hovered || preview) ? "C1_wallHighlighted" : undefined}
      stroke={selected ? "#ffa500" : interactive && hovered ? "#fed7aa" : c1_state.project.settings.wallColorInterior} strokeWidth={width} strokeLinecap="square" />
    {interactive && <g onPointerDown={event => startDrag(event, "move")}>
      <line data-testid={`c1-free-wall-${wall.id}-move`} x1={x(geometry.start)} y1={y(geometry.start)}
        x2={x(geometry.end)} y2={y(geometry.end)} stroke="transparent" strokeWidth={Math.max(width, 18 / zoom)} pointerEvents="stroke" />
    </g>}
    {selected && (["start", "end"] as const).map(handle => <g key={handle}
      onPointerDown={event => startDrag(event, handle)}>
      <circle data-testid={`c1-free-wall-${wall.id}-${handle}`} cx={x(geometry[handle])} cy={y(geometry[handle])}
        r={7 / zoom} fill="#fff" stroke={drag.current?.snapped && drag.current.handle === handle ? "#2563eb" : "#f59e0b"} strokeWidth={2 / zoom} />
    </g>)}
  </g>;
}
